/**
 * Unit conversion functions for Stage 2 (IR → ProseMirror).
 *
 * All IR values use raw OOXML units. This module converts them to
 * CSS-compatible values (pt, px) for ProseMirror node attributes.
 */

/** Convert OOXML twips (1/20 of a point) to points. */
export function twipsToPt(twips: number): number {
  return Math.round((twips / 20) * 100) / 100;
}

/** Convert OOXML twips to pixels (at 96 DPI). */
export function twipsToPx(twips: number): number {
  // 1 point = 1/72 inch, 1 twip = 1/20 point
  // pixels = twips / 20 * (96 / 72) = twips * 96 / 1440
  return Math.round((twips * 96) / 1440);
}

/** Convert OOXML half-points to points (e.g., 24 half-points = 12pt). */
export function halfPtToPt(halfPt: number): number {
  return halfPt / 2;
}

/** Convert OOXML eighths-of-a-point to points (for border widths). */
export function eighthsPtToPt(eighths: number): number {
  return Math.round((eighths / 8) * 100) / 100;
}

/** Convert OOXML EMUs (English Metric Units) to pixels (at 96 DPI). */
export function emuToPx(emu: number): number {
  // 1 inch = 914400 EMU, 1 inch = 96 px
  return Math.round((emu / 914400) * 96);
}

/** Format a point value as CSS string (e.g., "12pt"). */
export function ptString(pts: number): string {
  return `${pts}pt`;
}

/** Format twips as a CSS pt string (e.g., 240 twips → "12pt"). */
export function twipsToPtString(twips: number): string {
  return ptString(twipsToPt(twips));
}

/**
 * Convert OOXML line spacing to a multiplier.
 * For 'auto' rule: 240 = single (1.0), 360 = 1.5, 480 = double (2.0).
 */
export function lineSpacingToMultiplier(value: number, rule: string): number | undefined {
  if (rule === 'auto') {
    return value / 240;
  }
  // For 'exact' and 'atLeast', the value is in twips — just convert to points
  // but the multiplier doesn't directly apply. Return undefined and let caller handle.
  return undefined;
}
